import React, { useEffect, useState } from 'react';
import { Flex, Box, useDisclosure, useMediaQuery } from '@chakra-ui/react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FilterProvider } from '../../contexts/FilterContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import useOnboarding from '../../hooks/useOnboarding';
import GettingStartedChecklist from '../onboarding/GettingStartedChecklist';
import OnboardingTour from '../onboarding/OnboardingTour';
import AssistantWidget, { DOCK_H } from '../chat/AssistantWidget';
import Sidebar from './Sidebar';
import SettingsModal from '../settings/SettingsModal';
import useIsPhone from '../../hooks/useIsPhone';
import { PhoneShellProvider } from '../phone/PhoneShellContext';
import PhoneTopBar from '../phone/PhoneTopBar';
import PhoneTabBar from '../phone/PhoneTabBar';
import { OPEN_SETUP_EVENT } from '../phone/events';

const STORAGE_KEY = 'mikapital_sidebar_collapsed';

/**
 * Below this the sidebar's 220px is the most expensive thing on screen — the
 * Movements page in particular has a list and a detail panel to fit beside it
 * (MI-336). It collapses to its 60px rail on its own here, and expands again
 * when the window does: the stored preference is what the reader chose for a
 * window that has room, and a narrow one should not overwrite it.
 */
const SIDEBAR_AUTO_COLLAPSE_QUERY = '(max-width: 1100px)';

const ProtectedLayout = () => {
  const isPhone = useIsPhone();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [windowIsNarrow] = useMediaQuery(SIDEBAR_AUTO_COLLAPSE_QUERY);
  // An expansion asked for while narrow, which lasts as long as the window
  // does: the toggle has to keep working, but it must not rewrite a
  // preference the reader set for a wider window.
  const [expandedWhileNarrow, setExpandedWhileNarrow] = useState(false);
  const sidebarCollapsed = windowIsNarrow ? !expandedWhileNarrow : isCollapsed;

  useEffect(() => {
    if (!windowIsNarrow) setExpandedWhileNarrow(false);
  }, [windowIsNarrow]);

  const settingsDisclosure = useDisclosure();
  const [settingsSection, setSettingsSection] = useState('account');

  const navigate = useNavigate();
  const location = useLocation();

  // --- Onboarding (MI-297) ---
  const onb = useOnboarding();
  const { dismissChecklist, markTourSeen, markInviteTourSeen } = usePreferences();

  // QA/demo affordance: `?onboarding=force` sends you into onboarding on any
  // account.
  const forceWizard = new URLSearchParams(window.location.search).get('onboarding') === 'force';

  // Onboarding is a page now, and it blocks: a user who hasn't finished it has
  // nothing else to be doing. This layout wraps every app route, so sending
  // them away from here is both the entry point and the guard — and because
  // /onboarding sits outside the layout, it can't loop.
  useEffect(() => {
    if (onb.showWizard || forceWizard) navigate('/onboarding', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onb.showWizard, forceWizard]);

  const resumeOnboarding = () => navigate('/onboarding');

  // Home's setup chip on the phone (which has no checklist) resumes here. The
  // chip is dev's; where it leads is this branch's — the wizard modal it used
  // to open no longer exists, so it goes to the page like every other way in.
  useEffect(() => {
    const onOpenSetup = () => navigate('/onboarding');
    window.addEventListener(OPEN_SETUP_EVENT, onOpenSetup);
    return () => window.removeEventListener(OPEN_SETUP_EVENT, onOpenSetup);
  }, [navigate]);


  const toggleSidebar = () => {
    if (windowIsNarrow) {
      setExpandedWhileNarrow((prev) => !prev);
      return;
    }
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  const openSettings = (section = 'account') => {
    setSettingsSection(section);
    settingsDisclosure.onOpen();
  };

  // On the way to /onboarding: paint nothing rather than one frame of the app
  // the user cannot use yet.
  if (onb.showWizard || forceWizard) return null;

  // The phone shell: a top bar, the page, and a tab bar, with the document
  // itself scrolling. No sidebar, assistant dock, checklist card or tour: the
  // tour, with none of its targets on screen, would mark itself seen and
  // never run on the desktop.
  if (isPhone) {
    return (
      <FilterProvider>
        <PhoneShellProvider onOpenSettings={openSettings}>
          <Flex minHeight="100dvh" className="App">
            <PhoneTopBar />
            <Box
              flex="1"
              as="main"
              className="app-main-content"
              bg="redesign.bg.page"
              pt="calc(44px + env(safe-area-inset-top))"
              pb="calc(49px + env(safe-area-inset-bottom) + 16px)"
              pl="env(safe-area-inset-left)"
              pr="env(safe-area-inset-right)"
              // `clip`, not `hidden`: a hidden axis would make this box a
              // scroll container and the pages' sticky headers would stick
              // to it instead of the viewport.
              overflowX="clip"
              minW={0}
            >
              <Outlet />
            </Box>
            <PhoneTabBar />
          </Flex>
          <SettingsModal
            isOpen={settingsDisclosure.isOpen}
            onClose={settingsDisclosure.onClose}
            initialSection={settingsSection}
          />
        </PhoneShellProvider>
      </FilterProvider>
    );
  }

  return (
    <FilterProvider>
      <Flex minHeight="100vh" className="App" bg="redesign.bg.chrome">
        {/* Sidebar */}
        <Sidebar
          isCollapsed={sidebarCollapsed}
          toggleSidebar={toggleSidebar}
          onOpenSettings={openSettings}
          isSettingsOpen={settingsDisclosure.isOpen}
        />

        {/* Main Content: a panel 8px in from the window that scrolls on its
            own, so its frame and each page's sticky header stay where they
            are. A page that fills the screen takes --mk-page-h, not 100vh:
            the panel less its border and the assistant bar's room.
            `.App` is a column flexbox, where `flex: 1` would size the panel
            to its content and override the height; on a computer it is off. */}
        <Box
          flex={{ base: '1', md: 'none' }}
          as="main"
          className="app-main-content"
          bg="redesign.bg.page"
          ml={{ base: 0, md: sidebarCollapsed ? '60px' : '220px' }}
          mt={{ base: '44px', md: '8px' }}
          mr={{ md: '8px' }}
          mb={{ md: '8px' }}
          // The assistant bar sits on the chrome below the panel, so the
          // panel ends above it with its bottom edge showing.
          h={{ md: `calc(var(--mk-panel-h) - ${location.pathname !== '/chat' ? DOCK_H : 0}px)` }}
          // Flush to the panel's top edge, so a page's sticky header pins
          // there with nothing scrolling past above it.
          pt={{ md: 0 }}
          overflowY={{ md: 'auto' }}
          border={{ md: '1px solid' }}
          borderColor="redesign.border"
          borderRadius={{ md: '12px' }}
          minH={{ base: '100vh', md: 0 }}
          // Reserve the assistant bar's height so it never covers content.
          pb={{ base: location.pathname !== '/chat' ? `${DOCK_H}px` : 0, md: 0 }}
          sx={{ '--mk-page-h': `calc(var(--mk-panel-h) - 2px - ${location.pathname !== '/chat' ? DOCK_H : 0}px)` }}
          transition="margin-left 0.3s"
          overflowX="hidden"
          minW={0}
        >
          {onb.showChecklist && (
            <GettingStartedChecklist
              onb={onb}
              onResume={resumeOnboarding}
              onDismiss={dismissChecklist}
            />
          )}
          <Outlet />
        </Box>

        <SettingsModal
          isOpen={settingsDisclosure.isOpen}
          onClose={settingsDisclosure.onClose}
          initialSection={settingsSection}
        />

        {/* Phase 3: coach-mark tour, once, on the movements page (where they land). */}
        {onb.showTour && location.pathname === '/movements' && (
          <OnboardingTour onFinish={markTourSeen} />
        )}

        {/* An invited member's tour: once, after their own flow, in the space
            they were invited to — same place and rule as the owner's, its own
            "seen" key. The steps and copy follow their role there. The two
            never overlap: showTour excludes guests and showGuestTour needs one. */}
        {onb.showGuestTour && location.pathname === '/movements' && (
          <OnboardingTour
            variant="guest"
            role={onb.inviteRole}
            workspace={onb.invitedWorkspace}
            onFinish={markInviteTourSeen}
          />
        )}

        {/* Phase 3: persistent floating assistant (hidden on the full /chat page). */}
        {location.pathname !== '/chat' && (
          <AssistantWidget sidebarOffset={sidebarCollapsed ? 60 : 220} />
        )}
      </Flex>
    </FilterProvider>
  );
};

export default ProtectedLayout;
